import '@mui/lab/themeAugmentation';

import { createTheme as createMuiTheme, Theme } from '@mui/material/styles';
import { Localization } from '@mui/material/locale';
import variants from './variants';
import typography from './typography';
import breakpoints from './breakpoints';
import components from './components';
import shadows from './shadows';

type PaletteOverrides = {
  primaryMain?: string;
  secondaryMain?: string;
};

/**
 * Lightens a hex color by a given amount (0-100)
 */
const lightenColor = (hex: string, amount: number): string => {
  // Remove # if present
  const color = hex.replace('#', '');

  // Parse hex to RGB
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);

  // Lighten each component
  const newR = Math.min(255, Math.round(r + (255 - r) * (amount / 100)));
  const newG = Math.min(255, Math.round(g + (255 - g) * (amount / 100)));
  const newB = Math.min(255, Math.round(b + (255 - b) * (amount / 100)));

  // Convert back to hex
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

const createMiraTheme = (
  name: string,
  paletteOverrides?: PaletteOverrides,
  locale?: Localization,
): Theme => {
  let themeConfig = variants.find((variant) => variant.name === name);

  if (!themeConfig) {
    console.warn(new Error(`The theme ${name} is not valid`));
    themeConfig = variants[0];
  }

  const isDarkMode = (themeConfig.palette as { mode?: string }).mode === 'dark';

  // For dark mode with custom primary color, create a lighter version for text
  const primaryContrastText =
    isDarkMode && paletteOverrides?.primaryMain
      ? lightenColor(paletteOverrides.primaryMain, 70)
      : themeConfig.palette.primary.contrastText;

  const palette = {
    ...themeConfig.palette,
    primary: {
      ...themeConfig.palette.primary,
      ...(paletteOverrides?.primaryMain
        ? {
            main: paletteOverrides.primaryMain,
            contrastText: primaryContrastText,
          }
        : null),
    },
    secondary: {
      ...themeConfig.palette.secondary,
      ...(paletteOverrides?.secondaryMain
        ? { main: paletteOverrides.secondaryMain }
        : null),
    },
  };

  return createMuiTheme(
    {
      spacing: 4,
      breakpoints,
      // @ts-expect-error Theme components vary by version.
      components,
      typography,
      shadows,
      palette,
    },
    {
      name: themeConfig.name,
      header: themeConfig.header,
      footer: themeConfig.footer,
      sidebar: themeConfig.sidebar,
    },
    locale ?? {},
  );
};

export default createMiraTheme;
